import { ChevronRight, Upload } from 'lucide-react'

/**
 * ReferralNeeded — s106, canvas "Orella Referrals Screens" A2 (approved; copy approved 2026-10-16,
 * claude/ORELLA_REFERRAL_APP_COPY_v1.md). The patient chose a specialist whose plan rules need a
 * referral (utils/referralRequired, the API's _referral_required). Two ways forward, plus an upload:
 *   1. see my primary care doctor first (send the case to the PCP instead);
 *   2. ask my PCP for a referral (the specialist gets the case now; booking opens once it is on file);
 *   "I already have a referral · upload it".
 * Presentational: the share step owns what each choice does.
 */
const NAVY = '#15354F'
const BLUE = '#46A8F9'

export default function ReferralNeeded({ doctorName, pcp, plan, onSeePcp, onAsk, onUpload, t }) {
  const pcpName = pcp && pcp.name ? pcp.name : ''
  const card = { background: '#fff', border: '1px solid #DCE6EE', borderRadius: 16, padding: 14 }
  return (
    <div className="px-4 pt-4 pb-6 flex flex-col gap-3" data-testid="referral-needed">
      <div style={card} className="flex flex-col gap-1.5">
        {plan && (
          <span className="self-start text-[12px] font-bold rounded-full px-2.5 py-1" style={{ background: '#FEF3C7', color: '#92400E' }}>{plan}</span>
        )}
        <p className="text-[16px] font-extrabold m-0" style={{ color: NAVY }}>
          {t('referral.neededHeading', { defaultValue: 'Your plan needs a referral to see {{doctor}}', doctor: doctorName })}
        </p>
        <p className="text-[13px] leading-snug m-0" style={{ color: '#4E6577' }}>
          {pcpName
            ? t('referral.neededBody', { defaultValue: 'Your plan asks your primary care doctor, {{pcp}}, to refer you to a specialist first. You have two ways forward.', pcp: pcpName })
            : t('referral.neededBodyNoPcp', 'Your plan asks your primary care doctor to refer you to a specialist first. You have two ways forward.')}
        </p>
      </div>
      <button type="button" onClick={onSeePcp} data-cta="referral-see-pcp" className="text-left flex flex-col gap-1 active:scale-[0.99]" style={card}>
        <span className="self-start text-[12px] font-bold rounded-full px-2.5 py-1" style={{ background: '#E9F8FF', color: NAVY }}>{t('referral.option1', 'Option 1')}</span>
        <span className="text-[16px] font-extrabold" style={{ color: NAVY }}>{t('referral.option1Title', 'See my primary care doctor first')}</span>
        <span className="text-[13px] leading-snug" style={{ color: '#4E6577' }}>
          {pcpName
            ? t('referral.option1Body', { defaultValue: 'Send your case to {{pcp}} instead. They can treat you or refer you on.', pcp: pcpName })
            : t('referral.choosePcp', 'Choose your primary care doctor first')}
        </span>
      </button>
      <button type="button" onClick={onAsk} data-cta="referral-ask-pcp" className="text-left flex flex-col gap-1 active:scale-[0.99]"
              style={{ ...card, border: `1.5px solid ${BLUE}` }}>
        <span className="self-start text-[12px] font-bold rounded-full px-2.5 py-1" style={{ background: '#E9F8FF', color: NAVY }}>{t('referral.option2', 'Option 2')}</span>
        <span className="text-[16px] font-extrabold flex items-center gap-1" style={{ color: NAVY }}>
          {pcpName
            ? t('referral.option2Title', { defaultValue: 'Ask {{pcp}} for a referral to {{doctor}}', pcp: pcpName, doctor: doctorName })
            : t('referral.askTitle', 'Ask for a referral')}
          <ChevronRight size={16} />
        </span>
        <span className="text-[13px] leading-snug" style={{ color: '#4E6577' }}>
          {pcpName
            ? t('referral.option2Body', { defaultValue: 'Orella sends {{pcp}} your request. {{doctor}} gets your case now; you can book once the referral is on file.', pcp: pcpName, doctor: doctorName })
            : t('referral.bookNote', { defaultValue: '{{doctor}} receives your case now. Booking with {{doctor}} opens once the referral is on file.', doctor: doctorName })}
        </span>
      </button>
      <button type="button" onClick={onUpload} data-cta="referral-have-one" className="text-[14px] font-bold py-2.5 flex items-center justify-center gap-1.5" style={{ color: '#1E6FB8' }}>
        <Upload size={15} /> {t('referral.haveReferral', 'I already have a referral · upload it')}
      </button>
    </div>
  )
}
